import React, { useContext, useEffect, useRef, useState } from "react";
import { IconButton, Box, CircularProgress } from "@mui/material";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import styles from "./styles.module.css";
import { UBButton, UBInput } from "../../../Libraries";
import { useSnackbar } from "../../../context/SnackbarContext";
import { LanguageContext } from "../../../context/LanguageContext";
import galleryPlaceholder from "../../../assets/images/galleryPlaceholder.png";
import ProductSuggestion from "../ProductSuggestion";

const MAX_QUANTITY_DIGITS = 7;

export default function AddQuickStockItem({
  onSubmit = () => {},
  setIsShowQuickStockForm,
  stockName = "",
  setStockName,
  isShowQuickStockForm,
}) {
  const [I18n] = useContext(LanguageContext);
  const [state, setState] = useState({
    name: stockName,
    qty: 1,
    price: "",
    imagePath: "",
  });
  const [errors, setErrors] = useState({
    name: false,
    price: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  const { showSnackbar } = useSnackbar();
  const nameRef = useRef(null);
  const qtyRef = useRef(null);
  const componentRef = useRef(null); // Ref for the entire component

  useEffect(() => {
    setState((prevState) => ({ ...prevState, name: stockName }));
  }, [stockName]);

  const stockValidation = () => {
    let obj = {};
    if (!state.name || !state.name.trim()) obj = { ...obj, name: true };
    if (!Number(state.price)) obj = { ...obj, price: true };

    return obj;
  };

  const handleInputChange = (key, value) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: false,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const { name, qty, price, imagePath } = state;
      if (Object.values(stockValidation()).length) {
        setErrors(stockValidation());
        setIsLoading(false);
        return;
      }
      const payload = {
        name,
        qty: Number(qty || 0),
        price: Number(price || 0),
        image_url: imagePath || null,
      };
      await onSubmit(payload);
      onResetField();
      setIsShowQuickStockForm(false);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const onResetField = () => {
    setState({
      name: "",
      qty: 1,
      price: "",
      imagePath: "",
    });
    setErrors({
      name: false,
      price: false,
    });
    setStockName("");
    if (nameRef?.current?.focus) {
      nameRef?.current?.focus();
    }
  };

  useEffect(() => {
    if (isShowQuickStockForm) {
      nameRef?.current?.focus();
    }
  }, [isShowQuickStockForm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isShowQuickStockForm &&
        componentRef.current &&
        !componentRef.current.contains(event.target)
      ) {
        handleSubmit(); // Trigger submit when clicking outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [state, isShowQuickStockForm]);

  const pickImageFromGallery = (photos) => {
    const file = photos[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (validTypes.includes(file.type)) {
        triggerLogEvent(analytics, "stock_add_image", {});
        for (let img of photos) {
          let reader = new FileReader();
          reader.readAsDataURL(img);
          reader.onload = function () {
            setState((prevState) => ({
              ...prevState,
              imagePath: reader.result,
            }));
          };
        }
      } else {
        showSnackbar({
          message: "Please select a valid image file (JPEG, PNG, or JPG).",
          severity: "error",
          icon: "",
        });
      }
    }
  };

  const onCloseStockForm = () => {
    onResetField();
    setIsShowQuickStockForm(false);
  };

  const isReadyToSave = () =>
    !!(state.name?.trim() && state.price && state.qty);
  return (
    <Box
      ref={componentRef}
      className={`${styles.quickAddstockItem} ${isShowQuickStockForm ? styles.quickAddstockItemShow : styles.quickAddstockItemHide}`}
    >
      <Box className={styles.quickAddstockItemRow}>
        <Box
          className={`${styles.addStockItemImage} ${state.imagePath ? styles.addStockItemImageActive : styles.addStockItemImageInactive}`}
        >
          {state.imagePath ? (
            <img src={state.imagePath} alt="transaction" />
          ) : (
            <img src={galleryPlaceholder} alt="" />
          )}
          <input
            name="image"
            type="file"
            accept=".jpeg, .png, .jpg"
            onChange={(e) => pickImageFromGallery(e.target.files)}
            id="image_upload"
          />
        </Box>
        <Box className={`${styles.addStockItemName}`}>
          <ProductSuggestion
            inputId="add_quick_stock_search_input"
            inputRef={nameRef}
            name={state.name}
            setName={(value) => handleInputChange("name", value)}
            placeholder={I18n.item_name}
            onKeyDown={handleKeyDown}
            className={`inputStock ${errors?.name ? "error" : ""}`}
            maxLength={100}
            handleSetProduct={(item) => {
              handleInputChange("name", item.product_name);
              handleInputChange("price", item.price);
              handleInputChange("qty", 1);
              qtyRef.current.focus();
            }}
          />
        </Box>
        <Box className={`${styles.addStockItemQuantity}`}>
          <UBInput
            inputId="add_quick_stock_quantity_input"
            className={`inputStock`}
            baseClassname={styles.baseInputStock}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            value={state.qty}
            placeholder={I18n.qty}
            name="qty"
            maxLength={MAX_QUANTITY_DIGITS}
            onlyNumbers={true}
            inputRef={qtyRef}
          />
        </Box>
        <Box className={`${styles.addStockItemPrice}`}>
          <UBInput
            inputId="price_id"
            className={`inputStock ${errors?.price ? "error" : ""}`}
            baseClassname={styles.baseInputStock}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            value={state.price}
            placeholder={I18n.sell_price}
            name="price"
            maxLength={7}
            onlyNumbers={true}
          />
        </Box>
        {/* <Box className={`${styles.addStockItemInputWrapper}`}>
        </Box> */}

        <Box className={`${styles.addStockItemAction}`}>
          {isLoading ? (
            <Box p={1}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <UBButton
              id={"add_quick_stock_action_btn"}
              title={isReadyToSave() ? I18n.save : I18n.cancel}
              className={
                isReadyToSave()
                  ? styles.ubPrimaryButton
                  : `newFontColor ${styles.clearAllBtn}`
              }
              onClick={() =>
                isReadyToSave() ? handleSubmit() : onCloseStockForm()
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
